/**
 * File: deviceUtils.ts
 * Path: apps/admin/src/shared/utils/deviceUtils.ts
 * Description: What this browser is and whether it can get notifications,
 * from what the browser tells us. Pure, so the decisions are tested without
 * a real phone.
 */

export interface BrowserFacts {
  userAgent: string;
  maxTouchPoints: number;
  /** Opened from the Home Screen / installed app rather than a browser tab. */
  installed: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  /** Notification.permission, when there is a Notification API. */
  permission: NotificationPermission | null;
  /** The Web Push key is in the build (VITE_FIREBASE_VAPID_KEY). */
  configured: boolean;
}

export type PushReadiness =
  /** Can be switched on (or already is). */
  | "ready"
  /** iPhone or iPad in a browser tab: Apple only allows it from the Home Screen app. */
  | "ios-install"
  /** Notifications were refused for this site in the browser's settings. */
  | "blocked"
  /** This browser can't receive web push at all. */
  | "unsupported"
  /** The build has no Web Push key yet (one-time Firebase setup). */
  | "not-configured";

export const isAppleMobile = ({ userAgent, maxTouchPoints }: Pick<BrowserFacts, "userAgent" | "maxTouchPoints">) =>
  /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);

export const pushReadiness = (facts: BrowserFacts): PushReadiness => {
  if (!facts.configured) return "not-configured";
  const pushApis = facts.hasServiceWorker && facts.hasPushManager && facts.hasNotification;
  if (isAppleMobile(facts) && !facts.installed) return "ios-install";
  if (!pushApis) return "unsupported";
  if (facts.permission === "denied") return "blocked";
  return "ready";
};

const deviceName = ({ userAgent, maxTouchPoints }: Pick<BrowserFacts, "userAgent" | "maxTouchPoints">): string => {
  if (/iPhone|iPod/.test(userAgent)) return "iPhone";
  if (/iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) return "iPad";
  if (/Android/.test(userAgent)) return /Mobile/.test(userAgent) ? "Android phone" : "Android tablet";
  if (/CrOS/.test(userAgent)) return "Chromebook";
  if (/Windows/.test(userAgent)) return "Windows PC";
  if (/Macintosh/.test(userAgent)) return "Mac";
  if (/Linux/.test(userAgent)) return "Linux PC";
  return "Device";
};

const browserName = (userAgent: string): string => {
  if (/Edg\//.test(userAgent)) return "Edge";
  if (/OPR\//.test(userAgent)) return "Opera";
  if (/SamsungBrowser/.test(userAgent)) return "Samsung Internet";
  if (/FxiOS|Firefox\//.test(userAgent)) return "Firefox";
  if (/CriOS|Chrome\//.test(userAgent)) return "Chrome";
  if (/Safari\//.test(userAgent)) return "Safari";
  return "Browser";
};

/** "iPhone, Home Screen app" or "Mac, Chrome": how the device is listed in Settings. */
export const deviceLabel = (facts: Pick<BrowserFacts, "userAgent" | "maxTouchPoints" | "installed">): string =>
  `${deviceName(facts)}, ${facts.installed ? "app" : browserName(facts.userAgent)}`;

/** Reads the facts from the real browser. */
export const readBrowserFacts = (): BrowserFacts => {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const win = typeof window === "undefined" ? undefined : window;
  const standalone =
    !!win?.matchMedia?.("(display-mode: standalone)").matches ||
    (nav as (Navigator & { standalone?: boolean }) | undefined)?.standalone === true;
  const hasNotification = !!win && "Notification" in win;
  return {
    userAgent: nav?.userAgent ?? "",
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    installed: standalone,
    hasServiceWorker: !!nav && "serviceWorker" in nav,
    hasPushManager: !!win && "PushManager" in win,
    hasNotification,
    permission: hasNotification ? Notification.permission : null,
    configured: Boolean(import.meta.env.VITE_FIREBASE_VAPID_KEY),
  };
};
